import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { MessageService } from 'primeng/api';
import { TranslocoService } from '@ngneat/transloco';
import { AuthService } from 'src/app/service/auth.service';
import { SocialService } from 'src/app/service/social.service';
import { ImageUrlService } from 'src/app/service/image-url.service';
import { ProjectService } from 'src/app/service/project.service';
import { UpdateProjectPutReq } from 'src/app/model/request/update-project-put-req';
import { sharingProjectType } from 'src/app/model/enum/sharingProjectType';
import { SocialReportDialogComponent } from '../social-report-dialog/social-report-dialog.component';
import { SocialBlockDialogComponent } from '../social-block-dialog/social-block-dialog.component';
import {
  ProjectProfileResponse,
  SocialFollowUserInfo,
  SocialPostResponse,
} from 'src/app/model/social/social.types';

type ProjectTab = 'posts' | 'info';

@Component({
  selector: 'app-project-profile',
  templateUrl: './project-profile.component.html',
  styleUrls: ['./project-profile.component.css'],
})
export class ProjectProfileComponent implements OnInit, OnDestroy {
  projectId = '';
  profile: ProjectProfileResponse | null = null;
  posts: SocialPostResponse[] = [];
  postsCursor: string | null = null;
  loading = true;
  postsLoading = false;
  followBusy = false;
  notFound = false;
  needsLogin = false;
  currentUserId?: string;
  currentUserAvatarUrl: string | null = null;
  projectAvatarLoadError = false;
  ownerAvatarLoadError = false;

  /** Aba ativa: posts (default) ou infos do projeto. */
  activeTab: ProjectTab = 'posts';

  /**
   * Set repassado aos `app-social-post-card` desta página: contém o `projectId` corrente,
   * portanto o botão "Seguir" do card fica oculto — o controle de seguir do projeto
   * fica no header desta tela.
   */
  postCardFollowedSet: Set<string> = new Set<string>();

  /** Dialog dos seguidores. */
  followersDialogVisible = false;
  followersList: SocialFollowUserInfo[] = [];
  followersListLoading = false;
  followersListTotal = 0;

  /** Kebab de ações do projeto (compartilhar / ajustar visibilidade). */
  showProjectActions = false;
  showShareModal = false;
  showVisibilityDialog = false;
  savingVisibility = false;
  @ViewChild('projectKebab') private projectKebab?: ElementRef<HTMLElement>;
  @ViewChild('reportDialog') private reportDialog?: SocialReportDialogComponent;

  /** Denuncia o DONO do projeto (a denúncia é sempre da pessoa, nunca do projeto). */
  reportOwner(): void {
    this.showProjectActions = false;
    if (this.profile) this.reportDialog?.open('User', this.profile.ownerUserId);
  }

  @ViewChild('blockDialog') private blockDialog?: SocialBlockDialogComponent;

  /** Abre a confirmação de bloqueio do DONO do projeto (o diálogo explica o efeito, faz a chamada e mostra o spinner). */
  blockOwner(): void {
    this.showProjectActions = false;
    if (!this.profile) return;
    this.blockDialog?.open(this.profile.ownerUserId, this.ownerDisplay);
  }

  /** Dono bloqueado: os projetos dele (este incluído) somem para o viewer, então volta ao feed. */
  onOwnerBlocked(): void {
    this.router.navigate(['/feed']);
  }

  /** Fecha o kebab ao clicar em qualquer ponto fora dele (o clique no próprio botão só alterna). */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.showProjectActions) return;
    const target = event.target as Node | null;
    if (target && this.projectKebab?.nativeElement.contains(target)) return;
    this.showProjectActions = false;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.showProjectActions = false;
  }

  private postObserver: IntersectionObserver | undefined;

  @ViewChild('postSentinel') set postSentinelRef(el: ElementRef | undefined) {
    this.postObserver?.disconnect();
    this.postObserver = undefined;
    if (el?.nativeElement) {
      this.postObserver = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) this.loadMore();
        },
        { rootMargin: '200px' }
      );
      this.postObserver.observe(el.nativeElement);
    }
  }

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private social: SocialService,
    private auth: AuthService,
    private messageService: MessageService,
    private translocoService: TranslocoService,
    private imageUrlService: ImageUrlService,
    private projectService: ProjectService,
    private location: Location
  ) {}

  ngOnInit(): void {
    this.currentUserId = this.auth.getUserInformations();
    this.route.paramMap.subscribe((pm) => {
      const id = pm.get('projectId');
      if (!id) {
        this.router.navigate(['/feed']);
        return;
      }
      this.projectId = id;
      this.postCardFollowedSet = new Set<string>([id]);
      this.loadAll();
    });
  }

  ngOnDestroy(): void {
    this.postObserver?.disconnect();
  }

  loadAll(): void {
    this.loading = true;
    this.notFound = false;
    this.needsLogin = false;
    this.projectAvatarLoadError = false;
    this.ownerAvatarLoadError = false;
    this.social.getProjectProfile(this.projectId).subscribe({
      next: (p) => {
        this.profile = p;
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        if (err?.status === 401 || err?.status === 0) {
          this.needsLogin = true;
          return;
        }
        if (err?.status === 404) {
          this.notFound = true;
          return;
        }
        this.messageService.add({
          key: 'toastAccount',
          severity: 'error',
          summary: this.translocoService.translate('socialFeed.profileLabel'),
          detail: SocialService.parseHttpErrorMessage(err),
        });
      },
    });
    this.loadPosts(true);
  }

  goLoginReturn(): void {
    this.auth.setPostLoginRedirect(this.router.url);
    this.router.navigate(['/login']);
  }

  goCreateAccount(): void {
    this.auth.setPostLoginRedirect(this.router.url);
    this.router.navigate(['/create-account']);
  }

  loadPosts(reset: boolean): void {
    if (reset) {
      this.posts = [];
      this.postsCursor = null;
    }
    this.postsLoading = true;
    this.social
      .getProjectPosts(this.projectId, 20, reset ? undefined : this.postsCursor)
      .subscribe({
        next: (res) => {
          this.posts = reset ? res.items : [...this.posts, ...res.items];
          this.postsCursor = res.nextCursor ?? null;
          this.postsLoading = false;
        },
        error: () => {
          this.postsLoading = false;
        },
      });
  }

  loadMore(): void {
    if (!this.postsCursor || this.postsLoading) return;
    this.loadPosts(false);
  }

  toggleFollow(): void {
    if (!this.profile || this.followBusy) return;
    this.followBusy = true;
    const wasFollowing = this.profile.isFollowedByViewer;

    // Optimistic update
    this.profile.isFollowedByViewer = !wasFollowing;
    this.profile.followersCount = Math.max(
      0,
      this.profile.followersCount + (wasFollowing ? -1 : 1)
    );

    const op = wasFollowing
      ? this.social.unfollowProject(this.projectId)
      : this.social.followProject(this.projectId);

    op.subscribe({
      next: () => {
        this.followBusy = false;
      },
      error: (err) => {
        // Revert
        if (this.profile) {
          this.profile.isFollowedByViewer = wasFollowing;
          this.profile.followersCount = Math.max(
            0,
            this.profile.followersCount + (wasFollowing ? 1 : -1)
          );
        }
        this.followBusy = false;
        this.messageService.add({
          key: 'toastAccount',
          severity: 'error',
          summary: this.translocoService.translate('socialFeed.follow'),
          detail: SocialService.parseHttpErrorMessage(err),
        });
      },
    });
  }

  goToOwnerProfile(): void {
    if (this.profile?.ownerUserId) {
      this.router.navigate(['/feed/profile', this.profile.ownerUserId]);
    }
  }

  goCreatePost(): void {
    this.router.navigate(['/feed'], {
      queryParams: { compose: this.projectId },
    });
  }

  /** Volta para a tela ANTERIOR (perfil do usuário, feed, etc.) em vez de forçar o feed. */
  goBackToFeed(): void {
    if (window.history.length > 1) this.location.back();
    else this.router.navigate(['/feed']);
  }

  onPostChanged(): void {
    this.loadPosts(true);
  }

  /**
   * Só faz sentido publicar em um projeto que aparece no feed. Projeto privado
   * some com o botão "Publicar" (o dono ainda pode torná-lo público na visibilidade).
   */
  get canPublish(): boolean {
    return this.profile?.sharingProjectTypeId !== sharingProjectType.PRIVATE;
  }

  get ownerDisplay(): string {
    if (!this.profile) return '';
    return (
      this.profile.ownerNickname ||
      this.profile.ownerName ||
      this.profile.ownerUserId.substring(0, 8) + '...'
    );
  }

  get vehicleLabel(): string {
    if (!this.profile) return '';
    const parts = [this.profile.vehicleManufacturer, this.profile.vehicleModel].filter(
      (p) => !!p
    );
    return parts.join(' ');
  }

  get ownerInitial(): string {
    return this.ownerDisplay.charAt(0).toUpperCase();
  }

  get ownerAvatarTransformedUrl(): string | null {
    return this.imageUrlService.transformSocialUrl(this.profile?.ownerAvatarUrl, 'avatar');
  }

  get projectAvatarTransformedUrl(): string | null {
    return this.imageUrlService.transformSocialUrl(this.profile?.avatarUrl, 'social');
  }

  get isAnonymous(): boolean {
    return !this.currentUserId;
  }


  switchTab(tab: ProjectTab): void {
    this.activeTab = tab;
  }

  /** Atributos exibidos como "specs" — exclui os já apresentados em campos dedicados. */
  get specsAttributeKeys(): string[] {
    if (!this.profile?.attributes) return [];
    const dedicated = new Set([
      'manufacturer',
      'vehicle-model',
      'date-of-manufacture',
      'detail',
      'photo',
      'primaryImage',
    ]);
    return Object.keys(this.profile.attributes).filter((k) => !dedicated.has(k));
  }

  /** Label amigável para uma família de VCU. Mantém o número se não houver mapeamento conhecido. */
  vcuFamilyLabel(familyId: number | null): string {
    if (familyId === null || familyId === undefined) return '';
    return `Family ${familyId}`;
  }

  showFollowers(): void {
    if (this.isAnonymous || !this.profile?.followersCount) return;
    this.followersDialogVisible = true;
    this.followersList = [];
    this.followersListLoading = true;
    this.social.getProjectFollowers(this.projectId, 1, 100).subscribe({
      next: (res) => {
        this.followersList = res?.users ?? [];
        this.followersListTotal = res?.total ?? this.followersList.length;
        this.followersListLoading = false;
      },
      error: () => {
        this.followersListLoading = false;
      },
    });
  }

  goToFollowerProfile(userId: string): void {
    this.followersDialogVisible = false;
    this.router.navigate(['/feed/profile', userId]);
  }

  followerDisplay(u: SocialFollowUserInfo): string {
    return u.nickname || u.name || (u.userId ? u.userId.substring(0, 8) + '…' : '?');
  }

  openShare(): void {
    this.showProjectActions = false;
    this.showShareModal = true;
  }

  openVisibility(): void {
    this.showProjectActions = false;
    this.showVisibilityDialog = true;
  }

  setProjectVisibility(typeId: number): void {
    if (!this.profile || this.savingVisibility) return;
    this.savingVisibility = true;
    this.projectService
      .updateProject(this.profile.ownerUserId, this.projectId, {
        sharingProjectTypeID: typeId,
      } as UpdateProjectPutReq)
      .subscribe({
        next: () => {
          this.savingVisibility = false;
          this.profile!.sharingProjectTypeId = typeId;
          this.showVisibilityDialog = false;
        },
        error: () => {
          this.savingVisibility = false;
        },
      });
  }
}
